import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Button, ButtonGroup, Divider } from '@nextui-org/react';

interface TipTapEditorProps {
    content: string;
    onChange: (content: string) => void;
    placeholder?: string;
    editable?: boolean;
    className?: string;
}

const TipTapEditor: React.FC<TipTapEditorProps> = ({
    content,
    onChange,
    placeholder = "Write your notes here...",
    editable = true,
    className = ''
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Disable features we don't need
                heading: false,
                codeBlock: false,
                code: false,
                blockquote: false,
                horizontalRule: false,
                dropcursor: false,
                gapcursor: false,
            }),
            Underline,
            Placeholder.configure({
                placeholder,
            }),
        ],
        content,
        editable,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm dark:prose-invert max-w-none p-4 min-h-[200px] focus:outline-none [&:focus-visible]:outline-none',
            },
        },
    });

    // Add styles to remove focus outline
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .ProseMirror:focus,
            .ProseMirror:focus-visible,
            .ProseMirror-focused {
                outline: none !important;
                box-shadow: none !important;
                border: none !important;
            }
            .ProseMirror {
                outline: none !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    if (!editor) {
        return null;
    }

    const EditorToolbar = () => {
        if (!editable) return null;

        return (
            <>
                <div className="flex items-center gap-1 p-2 border-b border-divider bg-content2">
                    <ButtonGroup size="sm" variant="flat">
                        <Button
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            isIconOnly
                            className={editor.isActive('bold') ? 'bg-primary text-primary-foreground' : ''}
                            aria-label="Bold"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 12h8a4 4 0 100-8H6v8zm0 0h9a4 4 0 010 8H6v-8z" />
                            </svg>
                        </Button>

                        <Button
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            isIconOnly
                            className={editor.isActive('italic') ? 'bg-primary text-primary-foreground' : ''}
                            aria-label="Italic"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4m0 16h-4m4-16l-4 16" />
                            </svg>
                        </Button>

                        <Button
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                            isIconOnly
                            className={editor.isActive('underline') ? 'bg-primary text-primary-foreground' : ''}
                            aria-label="Underline"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20h10M7 4v8a5 5 0 0010 0V4" />
                            </svg>
                        </Button>
                    </ButtonGroup>

                    <Divider orientation="vertical" className="mx-2 h-6" />

                    <ButtonGroup size="sm" variant="flat">
                        <Button
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                            isIconOnly
                            className={editor.isActive('bulletList') ? 'bg-primary text-primary-foreground' : ''}
                            aria-label="Bullet List"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 0m-7 6h7m-7 6h7m-11-12v.01m0 6v.01m0 6v.01" />
                            </svg>
                        </Button>

                        <Button
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            isIconOnly
                            className={editor.isActive('orderedList') ? 'bg-primary text-primary-foreground' : ''}
                            aria-label="Numbered List"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20h10M10 4h10M10 10h10M10 16h10M4 4v3m0-3h2m-2 7h2m0 0v1m0-1v-1m-2 7h2m0 0v3m0-3v-1" />
                            </svg>
                        </Button>
                    </ButtonGroup>

                    <div className="ml-auto flex items-center gap-2">
                        <ButtonGroup size="sm" variant="flat">
                            <Button
                                onClick={() => editor.chain().focus().undo().run()}
                                isIconOnly
                                isDisabled={!editor.can().undo()}
                                aria-label="Undo"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                            </Button>

                            <Button
                                onClick={() => editor.chain().focus().redo().run()}
                                isIconOnly
                                isDisabled={!editor.can().redo()}
                                aria-label="Redo"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
                                </svg>
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className={`rounded-lg overflow-hidden bg-content1 ${className}`}>
            <EditorToolbar />
            <EditorContent
                editor={editor}
                className="[&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:focus-visible:outline-none [&_.ProseMirror-focused]:outline-none [&_.ProseMirror-focused]:!shadow-none"
            />
        </div>
    );
};

export default TipTapEditor; 